import { Clear } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import useGetDefaultCodingProfile from 'api/codingProfile/useGetDefaultCodingProfile/useGetDefaultCodingProfile';
import useGetProductBranchsList from 'api/productDetail/useGetProductBranchList/useGetProductBranchList';
import useProductDetail from 'api/productDetail/useProductDetail/useProductDetail';
import CodingCreateDialog from 'components/shared/BranchSystemCreate/Dialog';
import CoddingMappingModal from 'components/shared/Modals/productsList/CoddingMapping/CoddingMapping';
import BranchProductGallery from 'components/shared/Modals/productsList/Gallery/BranchProductGallery/BranchProductGallery';
import OriginProductGallery from 'components/shared/Modals/productsList/Gallery/OriginProductGallery/OriginProductGallery';
import ManualProductCodding from 'components/shared/Modals/productsList/ProductCoddingEditModal/ProductCoddingEditModal';
import ProductAccordion from 'components/shared/productDetail/ProductAccordion/ProductAccordion';
import ProductDetailHeader from 'components/shared/productDetail/ProductDetailHeader/ProductDetailHeader';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import PermissionGuard from 'hooks/permissions/PermissionGuard';
import { permissions } from 'hooks/permissions/permissions';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import CreateProductCodding from '../../components/shared/Modals/productsList/CreateProductCodding/CreateProductCodding';

const ProductDetail = () => {
  const { guid } = useParams();
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState('100');
  const [searchValue, setSearchValue] = useState('');
  const [apiSearch, setApiSearch] = useState('');
  const [productGallery, setProductGallery] = useState(false);
  const [branchGallery, setBranchGallery] = useState(false);
  const [branchSystemEdit, setBranchSystemEdit] = useState(null);
  const [expand, setExpand] = useState(null);
  const [editManual, setEditManual] = useState(null);
  const [editMapping, setEditMapping] = useState(null);
  const [createCoddingOpen, setCreateCoddingOpen] = useState(false);
  const [createCoddingOriginGuid, setCreateCoddingOriginGuid] = useState(null);
  const [createCoddingBranchGuid, setCreateCoddinBranchGuid] = useState(null);
  const [createCoddingType, setCreateCoddingType] = useState(null);
  const [createCoddingProfileGuid, setCreateCoddingProfileGuid] =
    useState(null);

  const [getProductDetail, loading, productDetailObj, setProductDetailObj] =
    useProductDetail(guid);
  const [getProductBranchsList, branchsListLoading, branchsList] =
    useGetProductBranchsList(guid);
  const {
    getDefaultCodingProfile,
    codingProfile: defaultCodingProfile,
    loading: defaultCodingProfileLoading,
  } = useGetDefaultCodingProfile();

  useEffect(() => {
    if (!guid) return;
    getDefaultCodingProfile();
    getProductDetail();
    getProductBranchsList({ page, count: pageCount, search: '' });
  }, []);

  useEffect(() => {
    if (!guid) return;
    setPage(1);
    getProductBranchsList({ page: 1, count: pageCount, search: apiSearch });
  }, [apiSearch]);

  useEffect(() => {
    if (!guid) return;
    getProductBranchsList({ page, count: pageCount, search: apiSearch });
  }, [page, pageCount]);

  const refreshBranchsList = () => {
    if (!guid) return;
    try {
      getProductBranchsList({ page: 1, count: pageCount, search: apiSearch });
    } catch (err) {
      console.log('ERROR ON refreshBranchsList:', err);
    }
  };

  function handleConfirmBranchEdit() {
    setBranchSystemEdit(null);
    refreshBranchsList();
  }

  function handleEditManual() {
    setEditManual(null);
    refreshBranchsList();
  }

  function handleUpdateMapping() {
    setEditMapping(null);
    refreshBranchsList();
  }

  function onCreateCoddingSuccessful() {
    setCreateCoddingOpen(false);
    setCreateCoddingOriginGuid(null);
    setCreateCoddinBranchGuid(null);
    setCreateCoddingType(null);
    setCreateCoddingProfileGuid(null);
    getProductDetail();
    refreshBranchsList();
  }

  const handleSubmitSearch = e => {
    e.preventDefault();
    e.stopPropagation();
    setApiSearch(searchValue);
  };

  const handleEditDefaultBranch = defaultCode => {
    if (!defaultCode) return;

    const codingGuid = defaultCode.guid ?? defaultCode.id;
    const codingType = String(defaultCode.type ?? '');

    switch (codingType) {
      case '0':
        return setBranchSystemEdit(codingGuid);

      case '1':
        return setEditManual({
          ...defaultCode,
          id: codingGuid,
          guid: codingGuid,
        });

      case '2':
        return setEditMapping({
          ...defaultCode,
          id: codingGuid,
          guid: codingGuid,
        });

      default:
    }
  };

  const onInsertCoddingFromOrigin = type => {
    setCreateCoddingOpen(true);
    setCreateCoddingOriginGuid(guid);
    setCreateCoddinBranchGuid(null);
    setCreateCoddingType(type);
    setCreateCoddingProfileGuid(null);
  };

  const onInsertDefaultCoddingFromOrigin = profile => {
    const profileGuid = profile?.guid ?? profile?.key ?? profile?.id;
    const profileType = String(profile?.type ?? profile?.codeType ?? '');

    if (!profileGuid || !['0', '1', '2', '3'].includes(profileType)) return;

    setCreateCoddingOpen(true);
    setCreateCoddingOriginGuid(guid);
    setCreateCoddinBranchGuid(null);
    setCreateCoddingType(profileType);
    setCreateCoddingProfileGuid(profileGuid);
  };

  const productBranchbranchPermissions =
    permissions.general.categories.globalSettings.productManagement.productList
      .detail.branch;

  return (
    <>
      {loading ? (
        <SpinnerLoader />
      ) : (
        <>
          <ProductDetailHeader
            detail={productDetailObj}
            setProductGallery={() => setProductGallery(true)}
            setProductLabel={() => console.log(true)}
            guid={guid}
            defaultCodingProfile={defaultCodingProfile}
            defaultCodingProfileLoading={defaultCodingProfileLoading}
            onInsertCodding={onInsertCoddingFromOrigin}
            onInsertDefaultCodding={onInsertDefaultCoddingFromOrigin}
          />
          <PermissionGuard permission={productBranchbranchPermissions.view}>
            {!branchsList?.list?.length && !apiSearch ? null : (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  direction: 'rtl',
                  mb: 2,
                }}
              >
                <form onSubmit={handleSubmitSearch}>
                  <TextField
                    variant="outlined"
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    size="small"
                    label="جست و جو ..."
                    InputProps={{
                      // startAdornment: (
                      //   <IconButton onClick={props.onSearchSubmit}>
                      //     <Search fontSize="small" />
                      //   </IconButton>
                      // ),
                      endAdornment: (
                        <IconButton
                          title="Clear"
                          aria-label="Clear"
                          size="small"
                          style={{
                            visibility: searchValue ? 'visible' : 'hidden',
                          }}
                          onClick={() => {
                            setApiSearch('');
                            setSearchValue('');
                          }}
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </form>
                <Pagination
                  color="primary"
                  count={Math.ceil(branchsList?.totalCount / pageCount)}
                  page={page}
                  sx={{ direction: 'rtl' }}
                  onChange={(event, value) => setPage(value)}
                />
              </Box>
            )}
            {branchsListLoading ? (
              <SpinnerLoader />
            ) : (
              <div>
                <Paper
                  square
                  elevation={2}
                  sx={{
                    display: { xs: 'none', lg: 'flex' },
                    bgcolor: 'rgba(15, 30, 50, .05)',
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{
                      width: { xs: 'auto', lg: '400px', xl: '500px' },
                      mr: '70px',
                    }}
                    color="text.secondary"
                  >
                    نام
                  </Typography>
                  <Typography color="text.secondary">کدینگ</Typography>
                </Paper>

                {branchsList?.list?.map((branch, index) => (
                  <ProductAccordion
                    detail={branch}
                    keyIndex={index}
                    key={branch.guid}
                    getProductDetail={getProductDetail}
                    refreshProductBranchList={refreshBranchsList}
                    setBranchGallery={() => setBranchGallery(branch.guid)}
                    setSystemEdit={setBranchSystemEdit}
                    setManualEdit={setEditManual}
                    setEditMapping={setEditMapping}
                    setCreateCoddingGuid={() => {
                      setCreateCoddingOpen(true);
                      setCreateCoddinBranchGuid(branch.guid);
                      setCreateCoddingOriginGuid(null);
                      setCreateCoddingType(null);
                      setCreateCoddingProfileGuid(null);
                    }}
                    expanded={expand}
                    setExpand={() =>
                      setExpand(prev =>
                        branch.guid === prev ? null : branch.guid,
                      )
                    }
                    refetchDetails={() =>
                      getProductBranchsList({
                        page,
                        count: pageCount,
                        search: apiSearch,
                      })
                    }
                    handleEditDefaultBranch={handleEditDefaultBranch}
                  />
                ))}
              </div>
            )}
          </PermissionGuard>
        </>
      )}

      <OriginProductGallery
        open={productGallery}
        onClose={() => setProductGallery(false)}
        selected={guid}
      />

      <ManualProductCodding
        open={!!editManual}
        handleClose={() => setEditManual(null)}
        selectedGuid={editManual?.guid}
        onUpdate={handleEditManual}
      />

      <BranchProductGallery
        open={Boolean(branchGallery)}
        onClose={() => setBranchGallery(null)}
        selected={branchGallery}
      />

      <CoddingMappingModal
        open={!!editMapping}
        onClose={() => setEditMapping(null)}
        onUpdate={handleUpdateMapping}
        selectedGuid={editMapping?.guid}
      />

      <CodingCreateDialog
        open={!!branchSystemEdit}
        onClose={() => setBranchSystemEdit(null)}
        handleConfirm={handleConfirmBranchEdit}
        guid={branchSystemEdit}
      />

      <CreateProductCodding
        open={!!createCoddingOpen}
        codeType={createCoddingType}
        onClose={() => {
          setCreateCoddingOpen(false);
        }}
        branchGuid={createCoddingBranchGuid}
        originGuid={createCoddingOriginGuid}
        profileGuid={createCoddingProfileGuid}
        onSuccess={onCreateCoddingSuccessful}
      />
    </>
  );
};

export default ProductDetail;
